import { ref, watch, computed } from '@vue/composition-api'
import store from '@/shared/store'
import i18n from '@hw-core/libs/i18n'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'

import { onFetchingList } from '@/shared/services/users'
// import { filter } from 'lodash'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  // Table Handlers
  const hoiwaColumns = [
    { key: 'user', label: i18n.t('employee'), sortable: true },
    { key: 'tempContract', label: i18n.t('temp_contract'), sortable: true },
    { key: 'email', label: i18n.t('email'), sortable: true },
    { key: 'mobile', label: i18n.t('mobile'), sortable: true },
    { key: 'role', label: i18n.t('role'), sortable: true },
    { key: 'love', label: i18n.t('permitted'), sortable: true },
    { key: 'actions', label: i18n.t('actions') },
  ]
  const oiwaColumns = [
    { key: 'user', label: i18n.t('name'), sortable: true },
    { key: 'email', label: i18n.t('email'), sortable: true },
    { key: 'mobile', label: i18n.t('mobile'), sortable: true },
    { key: 'role', label: i18n.t('profession'), sortable: true },
    { key: 'actions', label: i18n.t('actions') },
  ]
  const perPage = ref(10)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const stateFilter = ref('')
  const cityFilter = ref([])
  const planFilter = ref(null)
  const statusFilter = ref('accepted')
  const ageFilter = ref(null)
  const subcontractorFilter = ref(null)
  const residencyFilter = ref('finland')
  let deleteId = null
  const loadMore = ref(true)
  const canLoadMore = ref(false)
  let users = []

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  const sendAcceptEmail = id => {
    store
      .dispatch('appPersonel/sendAcceptEmail', { id })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Sähköposti lähetetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const checkIfEmailTaken = async email => {
    const response = await store.dispatch('appPersonel/isEmailTaken', { email })
    return response
  }

  // eslint-disable-next-line no-shadow
  const addUsersFromCsv = async newUsers => {
    store
      .dispatch('appPersonel/addUsersFromCsv', { users: newUsers })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Työntekijät lisätty CSV-tiedostosta',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        users = []
        refetchData()
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const addUser = async userData => {
    const user = { ...userData }
    user.firstName = user.firstName.toLowerCase()
    user.lastName = user.lastName.toLowerCase()
    user.fullName = `${user.firstName} ${user.lastName}`
    user.status = 'accepted'
    user.carAvailable = user.carAvailable || false
    user.created = new Date()
    user.confirmed = true
    user.isAdmin = false
    user.files = []
    user.residency = 'finland'
    if (store.state.app.appConfig.type === 'hw-admin') {
      user.love = user.love === 'suoritettu'
    }

    const isEmailExist = await checkIfEmailTaken(user.email)
    if (isEmailExist === true) {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t('error'),
          text: 'Sähköposti on jo käytössä',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    } else {
      store
        .dispatch('appPersonel/addUser', user)
        .then(res => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('success'),
              text: 'Käyttäjä luotu',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          users = []
          refetchData()
          sendAcceptEmail(res.id)
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('error'),
              text: i18n.t('something_went_wrong'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }
  }

  watch(
    [
      currentPage,
      perPage,
      searchQuery,
      roleFilter,
      cityFilter,
      planFilter,
      statusFilter,
      stateFilter,
      ageFilter,
      subcontractorFilter,
      residencyFilter,
    ],
    (newVal, oldVal) => {
      if (newVal[0] === oldVal[0]) {
        users = []
      }
      refetchData()
    }
  )
  const deleteUser = id => {
    deleteId = id
  }

  const confirmDelete = () => {
    store
      .dispatch('appPersonel/deleteUser', { id: deleteId })
      .then(() => {
        users = []
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Käyttäjä poistettu',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }
  const fetchUsers = async () => {
    // fetchUsersProvider fn
    const fetchUsersAction = 'appPersonel/fetchUsers' // TODO refactor to constant
    
    const queryParams = {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      role: roleFilter.value,
      state: stateFilter.value,
      city: cityFilter.value,
      plan: planFilter.value,
      status: statusFilter.value,
      age: ageFilter.value,
      isSubcontractor: subcontractorFilter.value,
      residency: residencyFilter.value,
      list: users,
    }

    const { data, isMore } = await onFetchingList(store, toast, fetchUsersAction, queryParams)
    if (data && data.length > 0) {
      users = data.filter(user => user.status === undefined || user.status !== 'deleted')
      canLoadMore.value = isMore
      loadMore.value = false
    }
    return users
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  const tempContractIsActive = (startDate, endDate) => {
    const now = new Date().getTime()
    return startDate.toDate().getTime() < now && now < endDate.toDate().getTime()
  }

  const tempContractWasActive = endDate => {
    const now = new Date().getTime()
    return endDate.toDate().getTime() < now
  }

  const tempContractWillBeActive = startDate => {
    const now = new Date().getTime()
    return now < startDate.toDate().getTime()
  }

  return {
    fetchUsers,
    deleteUser,
    confirmDelete,
    hoiwaColumns,
    oiwaColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,
    loadMore,
    canLoadMore,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,
    addUser,
    addUsersFromCsv,
    sendAcceptEmail,
    checkIfEmailTaken,
    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
    stateFilter,
    cityFilter,
    ageFilter,
    subcontractorFilter,
    residencyFilter,

    tempContractIsActive,
    tempContractWasActive,
    tempContractWillBeActive,
  }
}
